import * as React from 'react';

import InfoIcon from '@mui/icons-material/Info';
import { useSnackbarContext } from '@skillzet/components';

// @ts-ignore: camelize package is not typed
import camelize from 'camelize';
import { useQuery } from 'react-query';

import { TIME_IN_MILLISECONDS } from '@app/common/constants/dateAndTimeConstants';
import { handleError } from '@app/common/helpers';
import axios, { AxiosResponse } from '@app/common/setup/axiosWithHeader';

import { PRIVACY_POLICY_THRESHOLD } from './constants';
import { IPrivacyPolicyBarProps, IPrivacyPolicyLogs, IPrivacyPolicyInfo } from './interfaces';

function differenceInDays(date1: Date, date2: Date): number {
  let differenceInTime = date1.getTime() - date2.getTime();

  return differenceInTime / TIME_IN_MILLISECONDS.DAY;
}

export async function getDismissedLogs(
  notificationId: number | undefined
): Promise<IPrivacyPolicyLogs[]> {
  return axios
    .get(`api_internal/v1/notifications/${notificationId?.toString() ?? ''}/logs`)
    .then(camelize)
    .then(({ data }: AxiosResponse<IPrivacyPolicyLogs[]>) => data)
    .catch(handleError);
}

export async function getPrivacyPolicyInfo(): Promise<IPrivacyPolicyInfo> {
  return axios
    .get('/privacy_policy_info')
    .then(camelize)
    .then(({ data }: AxiosResponse<IPrivacyPolicyInfo>) => data)
    .catch(handleError);
}

function PrivacyPolicyBar({ userId }: IPrivacyPolicyBarProps): JSX.Element {
  const { data: info } = useQuery<IPrivacyPolicyInfo>('privacyPolicyInfo', getPrivacyPolicyInfo, {
    staleTime: Infinity,
    cacheTime: Infinity,
    retry: false,
  });
  const { data: dismissedLogs } = useQuery<IPrivacyPolicyLogs[]>(
    'privacyPolicyLogs',
    () => getDismissedLogs(info?.notificationId),
    { staleTime: Infinity, cacheTime: Infinity, enabled: !!info?.notificationId, retry: false }
  );

  const { show } = useSnackbarContext();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const dismissSnackBar = async (event: React.SyntheticEvent, reason?: string) => {
    const notificationId = info?.notificationId ?? -1;
    const dismiss = {
      notificationId,
      action: 'dismissed',
      userId,
    };
    try {
      await axios.post(`api_internal/v1/notifications/${notificationId}/logs`, {
        log: dismiss,
      });
    } catch (error) {
      handleError(error);
    }
  };

  const checkLogs = (): boolean => {
    if (!info) {
      return false;
    }
    const lastUpdated = new Date(info.lastUpdated);

    if (differenceInDays(new Date(), lastUpdated) > PRIVACY_POLICY_THRESHOLD) {
      return false;
    }

    if (!dismissedLogs) {
      return false;
    }

    const lastDismissed = dismissedLogs[0];
    if (!lastDismissed) {
      return true;
    }

    return new Date(lastDismissed.createdAt) < lastUpdated;
  };

  React.useEffect(() => {
    const shouldShowSnackBar: boolean = checkLogs();
    if (shouldShowSnackBar) {
      show(
        'Our Privacy Policy has been updated',
        'info',
        {
          ctaContent: 'Check it out',
          duration: TIME_IN_MILLISECONDS.DAY,
          ctaOnClick: () => window.open('https://www.skillz.com/privacy-policy/'),
          otherOnCloseAction: dismissSnackBar,
          icon: <InfoIcon />,
        },
        { onClose: undefined }
      );
    }
  }, [info, dismissedLogs]);

  return <></>;
}

export default PrivacyPolicyBar;
